/**
 * Meal Data Normalization Utility
 * 
 * Standardizes meal objects to consistent field names.
 * Fixes the protein_g vs protein inconsistency across the codebase.
 */

/**
 * Normalize meal data to standard field names
 * 
 * Standard fields:
 * - calories (number)
 * - protein (number) - grams
 * - carbs (number) - grams  
 * - fat (number) - grams
 * - fiber (number) - grams
 * - food_items (string) - meal name/description
 * 
 * @param {object} meal - Raw meal data from any source
 * @returns {object} - Normalized meal object
 */
export const normalizeMeal = (meal) => {
  if (!meal) return null;
  
  return {
    // Identity
    id: meal.id || meal.meal_id || meal._id || null,
    food_items: meal.food_items || meal.name || meal.title || meal.description || '',
    
    // Macros - handle both naming conventions
    calories: Number(meal.calories) || 0,
    protein: Number(meal.protein ?? meal.protein_g) || 0,
    carbs: Number(meal.carbs ?? meal.carbs_g) || 0,
    fat: Number(meal.fat ?? meal.fat_g) || 0,
    fiber: Number(meal.fiber ?? meal.fiber_g) || 0,
    sugar: Number(meal.sugar ?? meal.sugar_g) || 0,
    sodium: Number(meal.sodium) || 0,
    
    // Metadata
    meal_type: meal.meal_type || meal.mealType || inferMealType(),
    source: meal.source || 'unknown',
    timestamp: meal.timestamp || meal.created_at || new Date().toISOString(),
    date: meal.date || new Date().toISOString().split('T')[0],
    
    // Preserve any extra fields
    ...extractExtraFields(meal)
  };
};

/**
 * Normalize meal for API submission (backend expects these field names)
 * @param {object} meal 
 * @returns {object}
 */
export const normalizeForApi = (meal) => {
  const normalized = normalizeMeal(meal);
  return {
    food_items: normalized.food_items,
    calories: normalized.calories,
    protein: normalized.protein,
    carbs: normalized.carbs,
    fat: normalized.fat,
    fiber: normalized.fiber,
    sugar: normalized.sugar,
    sodium: normalized.sodium,
    meal_type: normalized.meal_type,
    source: normalized.source
  };
};

/**
 * Normalize meal for guest storage (uses _g suffix for compatibility)
 * @param {object} meal 
 * @returns {object}
 */
export const normalizeForGuest = (meal) => {
  const normalized = normalizeMeal(meal);
  return {
    id: normalized.id || `guest_${Date.now()}`,
    food_items: normalized.food_items,
    calories: normalized.calories,
    protein_g: normalized.protein,
    carbs_g: normalized.carbs,
    fat_g: normalized.fat,
    timestamp: normalized.timestamp,
    source: 'guest'
  };
};

/**
 * Calculate totals from an array of meals
 * @param {array} meals 
 * @returns {object}
 */
export const calculateMealTotals = (meals) => {
  if (!Array.isArray(meals) || meals.length === 0) {
    return { calories: 0, protein: 0, carbs: 0, fat: 0 };
  }
  
  return meals.reduce((totals, meal) => {
    const normalized = normalizeMeal(meal);
    return {
      calories: totals.calories + normalized.calories,
      protein: totals.protein + normalized.protein,
      carbs: totals.carbs + normalized.carbs,
      fat: totals.fat + normalized.fat
    };
  }, { calories: 0, protein: 0, carbs: 0, fat: 0 });
};

/**
 * Infer meal type from current time
 * @returns {string}
 */
const inferMealType = () => {
  const hour = new Date().getHours();
  if (hour < 11) return 'breakfast';
  if (hour < 15) return 'lunch';
  if (hour < 21) return 'dinner';
  return 'snack';
};

/**
 * Extract fields that aren't part of standard normalization
 * @param {object} meal 
 * @returns {object}
 */
const extractExtraFields = (meal) => {
  const standardFields = [
    'id', 'meal_id', '_id', 'food_items', 'name', 'title', 'description',
    'calories', 'protein', 'protein_g', 'carbs', 'carbs_g', 'fat', 'fat_g',
    'fiber', 'fiber_g', 'sugar', 'sugar_g', 'sodium',
    'meal_type', 'mealType', 'source', 'timestamp', 'created_at', 'date'
  ];
  
  const extra = {};
  for (const key of Object.keys(meal)) {
    if (!standardFields.includes(key)) {
      extra[key] = meal[key];
    }
  }
  return extra;
};

export default {
  normalizeMeal,
  normalizeForApi,
  normalizeForGuest,
  calculateMealTotals
};
